import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import isEqual from "lodash/isEqual";
import { FeedbackType } from "../../../../../API";
import { IRecentEventsTableRow } from "../common/events.models";
import { useHasRoleAccess } from "../../../../../common/hooks/useCheckRoleAccess";
import FeedbackButton from "../../../../../common/components/item/FeedbackButton";
import { Tooltip, Typography } from "@mui/material";

interface FeedbackThumbsProps {
  params: IRecentEventsTableRow;
  disabled: boolean;
  onThumbUpClick: () => void;
  onThumbsDownClick: () => void;
}

const FeedbackThumbsComponent = ({
  params,
  disabled,
  onThumbUpClick,
  onThumbsDownClick,
}: FeedbackThumbsProps) => {
  const isDeniedAccess = useHasRoleAccess("readonly");

  const isDisabledAll = disabled || isDeniedAccess;

  const feedback = params?.feedback;
  const tooltipText = useMemo(() => {
    if (isDeniedAccess) return null;

    return "Feedback and Feedback details are available on the Control room (Beta) page";
  }, [isDeniedAccess]);

  const confirmNegativeFeedback = (): void => {
    if (feedback === FeedbackType.NEGATIVE) return;

    onThumbsDownClick();
  };

  return (
    <Tooltip
      title={
        tooltipText ? (
          <Typography variant="body2Regular">{tooltipText}</Typography>
        ) : null
      }
      placement="top-start"
      arrow
    >
      <Box
        sx={{
          display: "flex",
          gap: "0.5em",
          cursor: isDisabledAll ? "default" : "pointer",
        }}
      >
        <FeedbackButton
          type="up"
          feedback={feedback}
          isDisabled={isDisabledAll}
          onClick={onThumbUpClick}
          className="thumb-up"
          ariaLabel="thumb-up"
        />

        <FeedbackButton
          type="down"
          feedback={feedback}
          isDisabled={isDisabledAll}
          onClick={confirmNegativeFeedback}
          className="thumb-down"
          ariaLabel="thumb-down"
        />
      </Box>
    </Tooltip>
  );
};

const FeedbackThumbs = memo(FeedbackThumbsComponent, (prev, next) => {
  const isEqualFeedback = prev.params?.feedback === next.params?.feedback;
  const isEqualMitigatedRisk = isEqual(
    prev.params?.mitigatedRisk,
    next.params?.mitigatedRisk
  );

  return isEqualFeedback && isEqualMitigatedRisk;
});

export default FeedbackThumbs;
